import type { AIProvider, GenerateRequest, GenerateResult, ProviderInfo, StreamHandler, ToolCall, UsageStats } from './types'
import { AiError } from './types'
import { readJsonOrThrow } from './streaming'
import { fetchOrThrow } from './network'
import type { AiSettings } from '@/types/settings'
import { TEST_PROMPT } from './prompts'
import { track } from '@/composables/useEventTracker'

/**
 * Provider que enruta TODAS las llamadas a través de un proxy serverless
 * (ej. Cloudflare Worker, plantilla en `worker/` — Fase 6).
 *
 * La ventaja: la API key vive como secreto del servidor, no en el
 * navegador. El proxy recibe `{ provider, system, user, ... }` y
 * reenvía al proveedor elegido, devolviendo el stream SSE tal cual.
 *
 * El proxy decide el proveedor/modelo según `settings.ai`, así que
 * desde el cliente solo mandamos la petición neutra + el proveedor
 * que queremos usar.
 */
export class ProxyProvider implements AIProvider {
  readonly info: ProviderInfo

  constructor(
    private endpoint: string,
    private providerId: AiSettings['activeProvider'],
    private model: string,
    private token = '',
  ) {
    this.info = { provider: 'proxy', model }
  }

  /**
   * Base URL del proxy sin barra final. Si el usuario la tecleó con `/`
   * al final (p. ej. `https://x.workers.dev/`), al concatenar `${endpoint}/generate`
   * se formaría `//generate`, que el worker resuelve como 404 ("No encontrado").
   * Aquí la normalizamos para evitar ese caso.
   */
  private get base(): string {
    return this.endpoint.replace(/\/+$/, '')
  }

  /** Cabeceras de la petición, con el token de auth si está configurado. */
  private headers(): Record<string, string> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' }
    if (this.token) h['x-app-token'] = this.token
    return h
  }

  private buildBody(req: GenerateRequest, stream: boolean) {
    return {
      provider: this.providerId,
      model: this.model,
      system: req.system,
      user: req.user,
      // Historial multi-turno para chat contextual (lo reenvía al worker,
      // que debe soportarlo). Si no se proporciona, el worker usa system+user.
      messages: req.messages,
      temperature: req.temperature ?? 0.4,
      maxTokens: req.maxTokens ?? 1024,
      stream,
      // Function-calling: el worker debe reenviar tools/tool_choice al
      // proveedor y devolver toolCalls en el resultado. Si el worker no los
      // soporta aún, llegan undefined y el comportamiento es el de hoy.
      tools: req.tools,
      toolChoice: req.toolChoice,
    }
  }

  async stream(
    req: GenerateRequest,
    onToken: StreamHandler,
  ): Promise<GenerateResult> {
    this.requireEndpoint()

    const response = await fetchOrThrow(`${this.base}/generate`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify(this.buildBody(req, true)),
      signal: req.signal,
    }, 'Proxy')

    if (!response.ok) {
      await readJsonOrThrow(response)
      throw new AiError('Respuesta inválida del proxy.', response.status)
    }

    // El proxy normaliza la salida a NDJSON: { delta?: string, usage?: {...}, done?: true, toolCalls?: [...] }
    const result = await readProxyStream(response, req.signal, onToken)
    // Métrica global: tokens IA consumidos (sin contenido sensible).
    void track('ai_request', {
      provider: String(this.providerId),
      model: this.model,
      tokens: (result.usage.tokensIn ?? 0) + (result.usage.tokensOut ?? 0),
      stream: true,
    })
    return result
  }

  async generate(req: GenerateRequest): Promise<GenerateResult> {
    this.requireEndpoint()
    const response = await fetchOrThrow(`${this.base}/generate`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify(this.buildBody(req, false)),
      signal: req.signal,
    }, 'Proxy')
    const data = await readJsonOrThrow<{
      text: string
      usage?: { tokensIn?: number; tokensOut?: number }
      toolCalls?: ToolCall[]
    }>(response)
    const usage = {
      tokensIn: data.usage?.tokensIn ?? null,
      tokensOut: data.usage?.tokensOut ?? null,
    }
    // Métrica global: tokens IA consumidos (sin contenido sensible).
    void track('ai_request', {
      provider: String(this.providerId),
      model: this.model,
      tokens: (usage.tokensIn ?? 0) + (usage.tokensOut ?? 0),
      stream: false,
    })
    return { text: data.text, usage, toolCalls: data.toolCalls }
  }

  async testConnection(): Promise<void> {
    this.requireEndpoint()
    const result = await this.generate({ user: TEST_PROMPT, maxTokens: 5 })
    if (!result.text) throw new AiError('El proxy devolvió una respuesta vacía.')
  }

  private requireEndpoint() {
    if (!this.endpoint) {
      throw new AiError('No hay endpoint de proxy configurado. Ajústalo en Ajustes.')
    }
  }
}

/**
 * Lee el stream del proxy. Formato normalizado (NDJSON):
 *   {"delta":"..."}
 *   {"delta":"..."}
 *   {"done":true,"usage":{"tokensIn":12,"tokensOut":34},"toolCalls":[...]}
 *
 * `toolCalls` solo aparece si el worker soporta function-calling; si no,
 * queda undefined y el comportamiento es el de siempre (solo texto).
 */
async function readProxyStream(
  response: Response,
  signal: AbortSignal | undefined,
  onToken: StreamHandler,
): Promise<GenerateResult> {
  if (!response.body) throw new AiError('El proxy no devolvió cuerpo.')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let text = ''
  let usage: UsageStats = { tokensIn: null, tokensOut: null }
  let toolCalls: ToolCall[] | undefined

  try {
    while (true) {
      if (signal?.aborted) {
        reader.cancel().catch(() => {})
        throw new AiError('Generación cancelada.', 499)
      }
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue
        let chunk: {
          delta?: string
          done?: boolean
          usage?: { tokensIn?: number; tokensOut?: number }
          toolCalls?: ToolCall[]
        }
        try {
          chunk = JSON.parse(trimmed)
        } catch {
          continue
        }
        if (chunk.delta) {
          text += chunk.delta
          onToken(chunk.delta)
        }
        if (chunk.done) {
          if (chunk.usage) {
            usage = {
              tokensIn: chunk.usage.tokensIn ?? null,
              tokensOut: chunk.usage.tokensOut ?? null,
            }
          }
          if (chunk.toolCalls) toolCalls = chunk.toolCalls
        }
      }
    }
  } finally {
    reader.releaseLock()
  }

  return { text, usage, toolCalls }
}
